import { bbox, treesToGeoJson, zonesToGeoJson, INTERVENTION_TYPES, type TreeRow } from '@treetrack/protocol';
import { activeFlags, growthBySpecies, interventionRollup, latestCounts, latestZoneSurvival, survivalBySpecies } from '../aggregate';
import { dateTicks, dateX, lineChartSvg } from '../charts';
import { fmtDate, fmtNum, h, svgNode } from '../dom';
import { signedBasemapUrl, type ProjectBundle } from '../data';
import { L, speciesName, t } from '../i18n';
import { createMap, legendEntries, type BasemapKind, type MapHandles } from '../map';
import { toHash } from '../router';
import { card, conditionBadge, empty, pct, table } from './shared';

export interface View {
  el: HTMLElement;
  destroy?: () => void;
}

export function projectView(b: ProjectBundle): View {
  const root = h('div', { class: 'project' });
  const counts = latestCounts(b.latestRows);

  // Header line
  root.appendChild(
    h(
      'div',
      { class: 'project-head' },
      h('h1', null, h('span', { class: 'project-code' }, b.project.code), ' ', b.project.name),
      h(
        'div',
        { class: 'kpis' },
        kpi(t('project.trees', { n: '' }).trim(), String(b.trees.length)),
        kpi(t('survey.found'), String(counts.summary.found)),
        kpi(t('survey.alive'), String(counts.summary.alive)),
        kpi(t('survey.survival_rate'), pct(counts.summary.survivalRatePct)),
        kpi(t('dashboard.cumulative'), pct(counts.planted ? (counts.summary.alive / counts.planted) * 100 : null)),
      ),
    ),
  );

  // Map
  const mapEl = h('div', { class: 'map', id: 'map' });
  const basemapSel = h(
    'select',
    { class: 'input input-sm', 'aria-label': 'Basemap' },
    h('option', { value: 'satellite' }, t('map.satellite')),
    h('option', { value: 'streets' }, t('map.streets')),
    b.project.basemap_path ? h('option', { value: 'pack' }, t('map.offline_pack')) : null,
  );
  const legend = h(
    'div',
    { class: 'legend' },
    ...legendEntries().map((e) =>
      h(
        'span',
        { class: 'legend-item' },
        h('span', { class: 'swatch', style: { background: e.fill, borderColor: e.stroke } }),
        e.key === 'unmeasured' ? t('map.unmeasured') : t(`condition.${e.key}`),
      ),
    ),
  );
  root.appendChild(card(null, h('div', { class: 'map-tools' }, basemapSel, legend), mapEl));

  let handles: MapHandles | null = null;
  const zonesFC = zonesToGeoJson(b.zones);
  const treesFC = treesToGeoJson(b.trees, b.latest);
  const box = bbox(b.trees) ?? boundaryBbox(b) ?? bbox(b.zones.map((z) => ({ lat: z.centroid_lat, lng: z.centroid_lng })));

  // Create the map after the element is in the document.
  queueMicrotask(() => {
    handles = createMap(mapEl, 'satellite');
    handles.map.on('load', () => {
      handles!.setData(zonesFC, treesFC);
      handles!.fitTo(box);
    });
    handles.onTreeClick((treeId) => {
      location.hash = toHash({ name: 'tree', id: b.project.id, treeId });
    });
  });
  basemapSel.addEventListener('change', async () => {
    if (!handles) return;
    const kind = basemapSel.value as BasemapKind;
    const url = kind === 'pack' && b.project.basemap_path ? await signedBasemapUrl(b.project.basemap_path) : null;
    handles.setBasemap(kind, url);
    handles.map.once('load', () => handles!.setData(zonesFC, treesFC));
  });

  // Dashboard panels
  root.appendChild(h('div', { class: 'grid' }, survivalByZonePanel(b), survivalBySpeciesPanel(b), survivalBySurveyPanel(b), growthPanel(b), interventionsPanel(b), flagsPanel(b), rosterPanel(b)));
  root.appendChild(h('p', { class: 'hint' }, t('dashboard.as_of', { date: fmtDate(b.loadedAt) })));

  return {
    el: root,
    destroy: () => handles?.destroy(),
  };
}

function boundaryBbox(b: ProjectBundle): [number, number, number, number] | null {
  const poly = b.project.boundary_geojson;
  if (!poly) return null;
  const pts = poly.coordinates.flat().map(([lng, lat]) => ({ lat, lng }));
  return bbox(pts);
}

function kpi(label: string, value: string): HTMLElement {
  return h('div', { class: 'kpi' }, h('div', { class: 'kpi-value' }, value), h('div', { class: 'kpi-label' }, label));
}

function survivalByZonePanel(b: ProjectBundle): HTMLElement {
  const rows = latestZoneSurvival(b.zoneSurvival);
  if (rows.length === 0) return card(t('dashboard.survival_by_zone'), empty());
  return card(
    t('dashboard.survival_by_zone'),
    table(
      [t('field.zone'), t('field.date'), t('survey.found'), t('survey.alive'), t('survey.survival_rate'), L('Sembrados', 'Planted'), t('dashboard.cumulative')],
      rows.map((r) => [r.zone_label ?? '—', fmtDate(r.date), String(r.found), String(r.alive), pct(r.survival_rate_pct), String(r.planted), pct(r.cumulative_survival_pct)]),
    ),
  );
}

function survivalBySpeciesPanel(b: ProjectBundle): HTMLElement {
  const rows = survivalBySpecies(b.latestRows, b.species);
  if (rows.length === 0) return card(t('dashboard.survival_by_species'), empty());
  return card(
    t('dashboard.survival_by_species'),
    table(
      [t('field.species'), L('Sembrados', 'Planted'), t('survey.found'), t('survey.alive'), t('survey.survival_rate'), t('dashboard.cumulative')],
      rows.map((r) => [r.species ? speciesName(r.species) : '—', String(r.planted), String(r.measured), String(r.alive), pct(r.ratePct), pct(r.cumulativePct)]),
    ),
  );
}

function survivalBySurveyPanel(b: ProjectBundle): HTMLElement {
  const zoneLabel = new Map(b.zones.map((z) => [z.id, z.label]));
  const rows = b.summaries.slice().sort((x, y) => y.date.localeCompare(x.date));
  if (rows.length === 0) return card(t('dashboard.survival_by_survey'), empty());
  return card(
    t('dashboard.survival_by_survey'),
    table(
      [t('field.date'), t('field.zone'), t('field.team'), t('survey.found'), t('survey.alive'), t('condition.NF'), t('survey.survival_rate')],
      rows.map((r) => [fmtDate(r.date), r.zone_id ? (zoneLabel.get(r.zone_id) ?? '—') : '—', r.team_name ?? '—', String(r.found), String(r.alive), String(r.not_found), pct(r.survival_rate_pct)]),
    ),
  );
}

function growthPanel(b: ProjectBundle): HTMLElement {
  const groups = growthBySpecies(b.measurements, b.trees, b.surveys, b.species);
  const withData = groups.filter((g) => g.points.some((p) => p.medianHeightCm != null || p.medianDiameterCm != null));
  if (withData.length === 0) return card(t('dashboard.growth_by_species'), empty());
  const charts = withData.slice(0, 8).map((g) => {
    const dates = g.points.map((p) => p.date);
    const hSeries = { label: `${t('dashboard.height')} (cm)`, color: '#1b5e20', points: g.points.filter((p) => p.medianHeightCm != null).map((p) => ({ x: dateX(p.date), y: p.medianHeightCm as number })) };
    const dSeries = { label: `${t('dashboard.diameter')} (mm)`, color: '#f9a825', dashed: true, points: g.points.filter((p) => p.medianDiameterCm != null).map((p) => ({ x: dateX(p.date), y: Math.round((p.medianDiameterCm as number) * 10 * 10) / 10 })) };
    return h(
      'div',
      { class: 'chart-box' },
      h('h3', null, g.species ? speciesName(g.species) : '—', ' ', h('span', { class: 'hint' }, `(${t('dashboard.median')}, n=${Math.max(...g.points.map((p) => p.n))})`)),
      svgNode(lineChartSvg([hSeries, dSeries], { xTicks: dateTicks(dates) })),
    );
  });
  return card(t('dashboard.growth_by_species'), h('div', { class: 'charts' }, ...charts));
}

function interventionsPanel(b: ProjectBundle): HTMLElement {
  const rows = interventionRollup(b.interventions, b.trees, b.zones);
  if (rows.length === 0) return card(t('dashboard.interventions'), empty());
  const typesSeen = INTERVENTION_TYPES.filter((ty) => rows.some((r) => r.counts[ty]));
  return card(
    t('dashboard.interventions'),
    table(
      [L('Mes', 'Month'), t('field.zone'), ...typesSeen.map((ty) => t(`intervention.${ty}`)), `${t('intervention.watered')} (${t('unit.L')})`],
      rows.map((r) => [r.month, r.zone_label, ...typesSeen.map((ty) => String(r.counts[ty] ?? '')), fmtNum(r.wateredLitres, 0)]),
    ),
  );
}

function flagsPanel(b: ProjectBundle): HTMLElement {
  const rows = activeFlags(b.flags, b.trees, b.zones);
  if (rows.length === 0) return card(t('dashboard.flags'), empty(t('common.none')));
  return card(
    t('dashboard.flags'),
    table(
      [t('field.tag'), t('field.zone'), t('tree.flags')],
      rows.map((r) => [
        h('a', { href: toHash({ name: 'tree', id: b.project.id, treeId: r.tree_id }) }, r.tag),
        r.zone_label,
        h('ul', { class: 'flag-list' }, ...r.flags.map((f) => h('li', null, t(`tree.flag_${f}`)))),
      ]),
    ),
  );
}

function rosterPanel(b: ProjectBundle): HTMLElement {
  const zoneLabel = new Map(b.zones.map((z) => [z.id, z.label]));
  const species = new Map(b.species.map((s) => [s.id, s]));
  const search = h('input', { class: 'input', placeholder: t('roster.search'), type: 'search' });
  const body = h('div');
  const render = (q: string) => {
    const needle = q.trim().toUpperCase();
    const rows = b.trees.filter((tr) => !needle || tr.tag.includes(needle)).slice(0, 300);
    body.replaceChildren(
      rows.length
        ? table(
            [t('field.tag'), t('field.zone'), t('field.species'), t('field.height_cm'), t('field.diameter_cm'), L('Estado', 'Status'), t('field.date')],
            rows.map((tr: TreeRow) => {
              const l = b.latest.get(tr.id);
              return [
                h('a', { href: toHash({ name: 'tree', id: b.project.id, treeId: tr.id }) }, tr.tag),
                tr.zone_id ? (zoneLabel.get(tr.zone_id) ?? '—') : '—',
                speciesName(tr.species_id ? species.get(tr.species_id) : null),
                fmtNum(l?.height_cm, 0),
                fmtNum(l?.diameter_basal_cm, 2),
                conditionBadge(l?.condition),
                fmtDate(l?.survey_date),
              ];
            }),
          )
        : empty(t('roster.empty')),
    );
  };
  search.addEventListener('input', () => render(search.value));
  render('');
  return card(`${t('roster.title')} (${b.trees.length})`, search, body, h('p', { class: 'hint' }, L('Máximo 300 filas; usa la búsqueda.', 'Up to 300 rows; use the search.')));
}
